import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Tag} from 'antd';
import {HStack, VStack} from "native-base";
import AppText from "../../../../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../../../../styles/GlobalStyles";
import {isRTL, t} from "../../../../../../../services/i18n";
import {Colors} from "../../../../../../../styles/Colors";
import {FLOW_MANAGER_MODAL, openModal} from "../../../../../../../redux/modalManagerSlice";
import {modalFlowOptions} from "../../../../../Popups/ModalFlow/ModalFlowOptions";
import ActiveIndicator from "./ActiveIndicator";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {brands, regular} from '@fortawesome/fontawesome-svg-core/import.macro';
import {GlobalConsts} from '../../../../../../../styles/GlobalConsts';
import CustomButtonV2 from "../../../../../../common/CustomButtonV2";
import InfoTooltip from "../../../../../../common/InfoTooltip";
import {MetaWhatsappTemplateStatus} from '../../../../../../../Configs/DatabaseConsts';
import {isMobile} from "../../../../../../../services/Helpers";
import ResponsiveMenu from "../../../../../../common/ResponsiveMenu";
import {autoMessageTypes} from "../../SettingsConfig";
const AutoMessages = (props) => {
    const {
        messageType,
        header,
        subheader,
        sectionHeader,
        periodAmountTextBefore,
        periodAmountTextAfter,
        sessionType,
        cardRows,
        canDelete,
        onDelete,
    } = props;
    const dispatch = useDispatch();
    const [isCardHovered, setIsCardHovered] = useState(false);
    const autoActions = useSelector(state => state.box.box.auto_actions)
    const messageTypeAutoActions = useMemo(() => {
        if (cardRows !== undefined) return cardRows
        if (!autoActions?.length) return []
        return autoActions.filter(item => item.auto_actions.action === messageType && item.session_type === sessionType)
    }, [cardRows, autoActions, messageType, sessionType]);
    const messageTemplateId = messageTypeAutoActions[0]?.message_template?.id ?? null;
    const connetctToMetaWhatsapp = useSelector(state => state.box.box.integrations?.find(i => i.name === 'metaWhatsapp'))
    const whatsappMessageTypeAutoActions = useMemo(() => {
        if (!messageTypeAutoActions?.length) return null
        return messageTypeAutoActions.find(item => item.auto_action_type === 'WHATSAPP' && item.active) || null
    }, [messageTypeAutoActions]);

    const whatsappTemplateStatus = useMemo(() => {
        return whatsappMessageTypeAutoActions?.message_template?.whatsapp_template?.status || null
    }, [whatsappMessageTypeAutoActions]);

    const showWhatsappTemplateStatusTag = useMemo(() => {
        return !!whatsappMessageTypeAutoActions && !!connetctToMetaWhatsapp
    }, [whatsappMessageTypeAutoActions, connetctToMetaWhatsapp]);
    const hasActiveAction = useMemo(() => messageTypeAutoActions.some(item => item.active), [messageTypeAutoActions]);

    const description = useMemo(() => {
        const isBeforeOrAfterTrial =
            messageType === autoMessageTypes.BEFORE_TRIAL_CLASS ||
            messageType === autoMessageTypes.AFTER_TRIAL_CLASS
        const row = messageTypeAutoActions[0]
        if (isBeforeOrAfterTrial && row?.period_amount && row?.period_time_unit) {
            return `${periodAmountTextBefore} ${row.period_amount} ${t(`${row.period_time_unit.toLowerCase()}s`)} ${periodAmountTextAfter}`
        }
        return subheader
    }, [messageType, messageTypeAutoActions, periodAmountTextBefore, periodAmountTextAfter, subheader]);

    const openAutoMessageModal = () => {
        // Drawer/overlay often skips mouseOut, so CustomButtonV2 hover would stick after close.
        setIsCardHovered(false)
        dispatch(openModal({
            modalName: FLOW_MANAGER_MODAL,
            props: {
                initSelection: modalFlowOptions.SESSION_AUTO_MESSAGE,
                initProps: {messageType, periodAmountTextBefore, periodAmountTextAfter, sessionType: sessionType ?? null, messageTemplateId},
                target: messageTypeAutoActions,
            },
        }))
    }

    const handleDelete = (e) => {
        e?.stopPropagation?.()
        onDelete?.(messageTemplateId)
    }

    const whatsappTemplateStatuStyle = {
        [MetaWhatsappTemplateStatus.APPROVED]: {backgroundColor: Colors.newDesign.lightGreen, color: Colors.newDesign.mainGreen, borderColor: Colors.newDesign.white},
        [MetaWhatsappTemplateStatus.PENDING]: {backgroundColor: Colors.newDesign.lightLightPurple, color: Colors.newDesign.purpleText, borderColor: Colors.newDesign.white},
        [MetaWhatsappTemplateStatus.NOT_APPROVED]: {backgroundColor: Colors.newDesign.redTagBackground, color: Colors.newDesign.redTagText, borderColor: Colors.newDesign.white},
        [null]: {backgroundColor: Colors.newDesign.redTagBackground, color: Colors.newDesign.redTagText, borderColor: Colors.newDesign.white},
    }

    const whatsappTemplateStatusLabel = {
        [MetaWhatsappTemplateStatus.APPROVED]: t('meta-whatsapp-template-status-approved'),
        [MetaWhatsappTemplateStatus.PENDING]: t('meta-whatsapp-template-status-pending'),
        [MetaWhatsappTemplateStatus.NOT_APPROVED]: t('meta-whatsapp-template-status-not-approved'),
        [null]: t('meta-whatsapp-template-status-not-approved'),
    }

    const whatsappTemplateError = whatsappMessageTypeAutoActions?.message_template?.whatsapp_template?.error_message;

    const actionsIcon = canDelete ? (
        isMobile ? (
            <div onClick={(e) => e.stopPropagation()}>
                <ResponsiveMenu
                    arrow={false}
                    anchor={
                        <div style={{cursor: 'pointer', padding: '4px'}}>
                            <FontAwesomeIcon icon={regular('ellipsis-vertical')} fontSize={GlobalConsts.iconSize.XS}/>
                        </div>
                    }
                    items={[{title: t('edit'), id: 'edit', iconName: 'fa-pen', onPress: openAutoMessageModal},
                        {title: t('delete'), id: 'delete', iconName: 'fa-trash', onPress: handleDelete},
                    ]}
                />
            </div>
        ) : (
            <CustomButtonV2
                type="link"
                iconName={'fa-trash'}
                iconProps={{iconPrefix: GlobalConsts.iconPrefix.LIGHT, iconColor: Colors.newDesign.darkGrey}}
                onClick={handleDelete}
            />
        )
    ) : null;

    return (
        <VStack space={'0.7rem'}>
            {sectionHeader &&
            <HStack mt={'-0.5rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.standard16} boldFont>{sectionHeader.title}</AppText>
                {sectionHeader.tooltip && <InfoTooltip title={sectionHeader.tooltip}/>}
            </HStack>}
            <CustomButtonV2 onClick={openAutoMessageModal} override={({isHovered}) =>
                <HStack alignItems={isMobile ? 'flex-start' : 'center'} borderWidth={'1px'} borderColor={isHovered ? Colors.newDesign.mainBlue : Colors.borderColor} borderRadius={'5px'} padding={'15px'} flex={1} position={'relative'}>
                    <VStack space={'0.3rem'} flex={1}>
                        <HStack space={'0.5rem'} alignItems={'center'}>
                            <ActiveIndicator isActive={hasActiveAction}/>
                            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} boldFont>{header}</AppText>
                        </HStack>
                        <AppText fontSize={GlobalStyleAttributes.fonts.standard16}>{description}</AppText>
                    </VStack>
                    {actionsIcon}
                    {showWhatsappTemplateStatusTag && (
                        <HStack
                            style={{position: 'absolute', left: isRTL() ? '0' : 'auto', right: isRTL() ? 'auto' : '0', top: '8px'}}
                            alignItems="center"
                            space="4px"
                        >
                            <FontAwesomeIcon icon={brands('whatsapp')} color={Colors.newDesign.darkGrey}/>
                            {whatsappTemplateStatus == MetaWhatsappTemplateStatus.NOT_APPROVED && whatsappTemplateError && (
                                <InfoTooltip title={whatsappTemplateError}/>
                            )}
                            <Tag style={whatsappTemplateStatuStyle[whatsappTemplateStatus]}>
                                {whatsappTemplateStatusLabel[whatsappTemplateStatus]}
                            </Tag>
                        </HStack>
                    )}
                </HStack>
            }/>
        </VStack>
    );
};

export default AutoMessages;
